import { spainClosurePeriodSchema } from '@spendesk/bookkeeping-core-types';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import {
  getV2ApiCompanyIdBookkeepingSpainClosurePeriods,
  getV2ApiCompanyIdBookkeepingSpainClosurePeriodsPeriodIdDownloadUrl,
  postV2ApiCompanyIdBookkeepingSpainClosurePeriods,
} from 'src/client/spendesk-api';
import {
  type GetV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest,
  type PostV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest,
} from 'src/client/spendesk-api-types';
import { downloadFromUrl } from 'src/core/utils/fileDownloader';

import { getSpainClosurePeriodsQueryKey } from './query-key-registry';

export const useSpainClosurePeriods = (
  queryParams?: GetV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest['query'],
) => {
  const companyId = useCompanyId();

  const { isLoading, data } = useQuery({
    queryKey: getSpainClosurePeriodsQueryKey(companyId, queryParams),
    staleTime: 10 * 60 * 1000 /* 10min */,
    cacheTime: 10 * 60 * 1000 /* 10min */,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await getV2ApiCompanyIdBookkeepingSpainClosurePeriods({
        path: {
          companyId,
        },
        query: queryParams,
      });

      if (response.statusCode === 200) {
        return spainClosurePeriodSchema.array().parse(response.body.periods);
      }

      return [];
    },
  });

  return {
    isLoading,
    periods: data ?? [],
  };
};

export const useCreateSpainClosurePeriod = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async (
      body: PostV2ApiCompanyIdBookkeepingSpainClosurePeriodsRequest['body'],
    ) => {
      const response = await postV2ApiCompanyIdBookkeepingSpainClosurePeriods({
        path: {
          companyId,
        },
        body,
      });

      if (response.statusCode === 200) {
        return response.body;
      }

      if (response.statusCode === 400) {
        throw new Error(response.body.reason as string);
      }

      if (response.statusCode === 409) {
        throw new Error(response.body.reason);
      }

      throw new Error('Failed to create Spain closure period');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSpainClosurePeriodsQueryKey(companyId),
      });
    },
  });

  return {
    createPeriod: mutate,
    isPending: isLoading,
  };
};

export const useDownloadSpainClosurePeriod = () => {
  const companyId = useCompanyId();

  const { mutate, isLoading } = useMutation({
    mutationFn: async (periodId: string) => {
      const response =
        await getV2ApiCompanyIdBookkeepingSpainClosurePeriodsPeriodIdDownloadUrl(
          {
            path: { companyId, periodId },
          },
        );

      if (response.statusCode === 200) {
        const downloadUrl = response.body.downloadUrl;
        const filename = `spain-closure-period-${periodId}.zip`;
        downloadFromUrl(downloadUrl, filename);
        return downloadUrl;
      }

      if (response.statusCode === 404) {
        const error = response.body.reason as string;
        throw new Error(error || 'Failed to get download URL');
      }

      throw new Error('Unknown error');
    },
  });

  return {
    downloadPeriod: mutate,
    isPending: isLoading,
  };
};
